import { SingleValue } from "react-select";
import {
  ProjectDataProps,
  SelectOption,
  GalleryData,
  Links,
  ProjectTouchedFields,
  ProjectFormErrors,
} from "../../Components/common/types";
import { isGalleryData, isSelectOption, isLinks } from "../helperFunctions";
import { log } from "../logger";

//Project form validations
export function handleProjectFormBlur(
  name: keyof ProjectDataProps,
  value:
    | number
    | string
    | SingleValue<SelectOption>
    | GalleryData
    | Partial<Links>,
  touched: ProjectTouchedFields,
  setTouched: React.Dispatch<React.SetStateAction<ProjectTouchedFields>>,
  validateFieldWrapper: (
    name: keyof ProjectDataProps,
    value:
      | number
      | string
      | SingleValue<SelectOption>
      | GalleryData
      | Partial<Links>
  ) => void
) {
  // log("Touch state", touched);
  if (name === "links" && typeof value === "object") {
    setTouched((prevTouched) => {
      const updatedTouched = { ...prevTouched };
      const linksTouched = { ...updatedTouched.links };

      Object.keys(value as Links).forEach((key) => {
        linksTouched[key as keyof Links] = true;
      });

      return {
        ...prevTouched,
        links: linksTouched,
      };
    });
  } else {
    // For non-nested fields, update normally
    setTouched((prevTouched) => ({
      ...prevTouched,
      [name]: true,
    }));
  }

  // log("handleProjectFormBlur:", name, "value:", value);
  validateFieldWrapper(name, value);
}

//validate field on focus and then blur if left empty
export function ProjectvalidateField<ProjectDataProps>(
  name: keyof ProjectDataProps,
  value:
    | number
    | string
    | SingleValue<SelectOption>
    | GalleryData
    | Partial<Links>,
  errors: ProjectFormErrors,
  setErrors: (errors: ProjectFormErrors) => void,
  projectData: {
    dateStarted: number | null;
    endDate: number | null;
    isOngoing: boolean;
  }, // To compare start and end dates
    projects?: { name: string }[]
) {
  // log('validateField - name:', name, 'value:', value);
  const newErrors = { ...errors }; //collect all errors and show them at ones(maintain immutability)

  switch (name) {
    case "name":
       if (!value || typeof value !== "string") {
    newErrors.name = "Name is required";
  } else if (
    projects?.some(
      (project) =>
        project.name?.toLowerCase().trim() === value.toLowerCase().trim()
    )
  ) {
    newErrors.name = "Project name is used.";
  } else {
    newErrors.name = "";
  }
      break;
    case "dateStarted":
      if (!value) {
        newErrors.dateStarted = "Start date is required.";
      } else if (typeof value === "number") {
        //depends on the value that comes in, number or string, its using number right now
        const futureDate = new Date(value).getTime() > Date.now();
        newErrors.dateStarted = futureDate
          ? "Start date cannot be in the future."
          : "";
      }

      // Re-validate dependent endDate when dateStarted changes
      if (projectData.endDate && typeof value === "number") {
        const startDate = new Date(value).getTime();
        const futureEndDate =
          new Date(projectData.endDate).getTime() > Date.now();
        if (startDate > new Date(projectData.endDate).getTime()) {
          newErrors.endDate = "End date cannot be earlier than the start date.";
        } else if (futureEndDate) {
          newErrors.endDate = "Future end date is not allowed.";
        } else {
          newErrors.endDate = ""; // Clear error if valid
        }
      }
      break;
    case "endDate":
      // log("onGoing", projectData.isOngoing);
      if (projectData.isOngoing) {
        newErrors.endDate = ""; // Clear the error since the input is disabled
      } else if (value && typeof value === "number") {
        const endDate = new Date(value).getTime();
        if (endDate > Date.now()) {
          newErrors.endDate = "End date cannot be in the future.";
        } else if (
          projectData.dateStarted !== null &&
          endDate < projectData.dateStarted
        ) {
          newErrors.endDate = "End date cannot be earlier than the start date.";
        } else {
          newErrors.endDate = "";
        }
      } else if (!value) {
        newErrors.endDate =
          "End date is required unless the project is ongoing.";
      }
      break;
    case "type":
      newErrors.type =
        value && typeof value === "object" && "value" in value
          ? ""
          : "Type is required";
      break;
    case "description":
      newErrors.description = value ? "" : `Description is required`;
      break;
    case "galleryData":
      if (isGalleryData(value)) {
        const { imgData, vidData } = value;
        // Check if both imgData and vidData are empty
        const isImgDataEmpty = Array.isArray(imgData) && imgData.length === 0;
        const isVidDataEmpty = Array.isArray(vidData) && vidData.length === 0;

        if (isImgDataEmpty && isVidDataEmpty) {
          newErrors.galleryData = "Please upload at least one image or video.";
        } else {
          newErrors.galleryData = ""; // Clear error if either is provided
        }
      } else {
        newErrors.galleryData = "Invalid gallery data.";
      }
      break;
    case "links":
      // log(value)
      if (value && typeof value === "object") {
        const links = value as Partial<Links>; //individual links
        const linkErrors = { ...newErrors.links };

        // Iterate through all link fields to validate each
        for (const [key, linkValue] of Object.entries(links)) {
          if (!linkValue) {
            linkErrors[key as keyof Links] =
              "The link is optional but much better if provided.";
          } else {
            try {
              // Attempt to create a new URL object
              new URL(linkValue);
              // Clear error if the value is valid
              linkErrors[key as keyof Links] = ""; // No error
            } catch (err) {
              linkErrors[key as keyof Links] = "Please provide a valid URL.";
            }
          }
        }

        newErrors.links = linkErrors;
        // log('Link Errros Test', newErrors.links);
      }
      break;
    default:
      break;
  }

  setErrors(newErrors);
  // log("inside ProjectvalidateField - errors:", newErrors);
}

//validate on empty submision or if data is invalid
export function projectValidate<
  ProjectDataProps extends {
    dateStarted: number | null; // Explicitly require dateStarted and endDate as a number and onGoing as boolean
    endDate: number | null;
    isOngoing: boolean;
    [key: string]: unknown; // Allow other fields as needed Record<string, unknown>
  }
>( //TypeScript expects Object.keys() to operate on objects of type Record<string, unknown> or similar
  projectData: ProjectDataProps,
  errors: ProjectFormErrors,
  setErrors: (errors: ProjectFormErrors) => void,
  setTouched: (touched: ProjectTouchedFields) => void
): boolean {
  let projectFormIsValid = true;

  log("Project data during validiation", projectData);

  (Object.keys(projectData) as (keyof ProjectDataProps)[]).forEach((key) => {
    if (key === "isOngoing") {
      return;
    }

    const value = projectData[key];
    //perform validation for all project data field
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      isSelectOption(value) ||
      isGalleryData(value) ||
      isLinks(value)
    ) {
      ProjectvalidateField(key, value, errors, setErrors, projectData);
    }

    // ✅ Special Handling for `endDate`
    if (key === "endDate") {
      if (projectData.isOngoing) {
        // projectData.endDate = 0; // ✅ Set to 0 if ongoing
        errors.endDate = ""; // ✅ Remove error message
      } else if (!value) {
        projectFormIsValid = false;
        errors.endDate = "End date is required"; // ✅ Only required if isOngoing is false
      }
      return;
    }

    // Check if the fields are empty
    // Special handling for galleryData
    if (key === "galleryData" && isGalleryData(value)) {
      const hasImgData =
        Array.isArray(value.imgData) && value.imgData.length > 0;
      const hasVidData =
        Array.isArray(value.vidData) && value.vidData.length > 0;
      if (!hasImgData && !hasVidData) {
        projectFormIsValid = false;
        errors[key as keyof ProjectFormErrors] =
          "Gallery data must include at least one image or video.";
      } else {
        errors[key as keyof ProjectFormErrors] = ""; // Clear error if valid
      }
    }

    // Special handling for links
    else if (key === "links" && isLinks(value)) {
      const links = value as Partial<Links>;
      const linkErrors: Partial<Links> = { ...errors.links }; // Clone existing link errors
      let hasInvalidLinks = false;

      // Validate each link field
      Object.entries(links).forEach(([linkKey, linkValue]) => {
        if (!linkValue) {
          linkErrors[linkKey as keyof Links] =
            "The link is optional but much better if provided.";
        } else {
          try {
            new URL(linkValue); // Valid URL
            linkErrors[linkKey as keyof Links] = ""; // Clear previous error
          } catch {
            linkErrors[linkKey as keyof Links] = "Please provide a valid URL.";
            hasInvalidLinks = true;
          }
        }
      });

      errors.links = linkErrors as Links; // Update the errors object
      if (hasInvalidLinks) {
        projectFormIsValid = false; // Fail validation if any URL is invalid
      }
    }
    // Check if the fields are empty (generic case)
    else if (!value) {
      projectFormIsValid = false;
      errors[key as keyof ProjectFormErrors] = `${
        (key as string).charAt(0).toUpperCase() + (key as string).slice(1)
      } is required`;
      //a case of empty fields submitted
    }
  });

  log("Erros in validate", errors);
  setErrors(errors); //a case of empty fields submitted
  // Mark all fields as touched
  setTouched({
    name: true,
    dateStarted: true,
    endDate: true,
    type: true,
    description: true,
    galleryData: true,
    links: {
      liveDemo: true,
      github: true,
    },
  });

  return projectFormIsValid;
}
